/**
 * Centralized token refresh logic — ported from
 * ``ando-frontend/app/api/tokenRefresh.ts`` (v1.x).
 *
 * Flow:
 *   1. ApiClient hits 401 on a non-auth endpoint.
 *   2. It calls ``refreshAccessToken()`` which POSTs to
 *      ``${apiUrl}/auth/refresh`` (browser sends ``ando_session`` cookie).
 *   3. Backend validates session, refreshes OIDC tokens, returns new session
 *      via ``Set-Cookie`` — frontend never reads the tokens directly.
 *   4. Original request is retried with the refreshed cookies.
 *
 * Concurrency:
 *   - Only one refresh in-flight at a time.
 *   - Concurrent 401s queue up and resolve together when refresh completes.
 *   - On refresh failure, the queue rejects with the same error.
 *
 * Cross-target note (v2): we do NOT redirect to ``/login`` here. The
 * web/client app supplies an optional ``onAuthFailure`` callback which is
 * invoked on terminal failure — Next.js routes use ``router.push`` while
 * Tauri webviews use ``window.location.assign``.
 */

import { getEnv } from "../env.js";

/** Endpoints we never try to refresh — calling them with a stale cookie is fine. */
const AUTH_ENDPOINTS = [
  "/auth/refresh",
  "/auth/login/start",
  "/auth/login/password",
  "/auth/login/totp",
  "/auth/login/idp/start",
  "/auth/login/idp/callback",
  "/auth/login/idp/exchange",
  "/auth/register",
  "/auth/verify-email",
  "/auth/password/reset",
  "/auth/password/set",
  "/auth/logout",
  // Session probe — 401 is the expected "no session" answer; treating it
  // as a refresh-trigger causes the /login page to thrash when it
  // bootstraps with /auth/me on mount (May 26 2026 v1 incident; kept in
  // sync here to prevent the v2 regression).
  "/auth/me",
] as const;

export function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some((path) => url.includes(path));
}

type QueueEntry = {
  resolve: () => void;
  reject: (error: Error) => void;
};

let isRefreshing = false;
let refreshQueue: QueueEntry[] = [];
let onAuthFailureHandler: ((error: Error) => void) | null = null;

/**
 * Pluggable refresh strategy — set by the host app at boot.
 *
 * Default (``null``) keeps the cookie-based ``POST /auth/refresh`` flow used
 * by the Next.js BFF. Tauri shells register a handler that swaps the
 * OS-keychain-stored ``refresh_token`` against ``POST /auth/desktop/refresh``
 * and re-seals the rotated bundle. The handler resolves on success and
 * throws on failure — same contract as the default fetch path.
 */
let refreshStrategy: (() => Promise<void>) | null = null;

/**
 * Register a custom refresh strategy. Pass ``null`` to revert to the
 * default cookie-based path (web). Tauri shells call this once at boot
 * inside ``main.tsx`` so every subsequent 401 retry rotates the Bearer
 * token via the desktop endpoint instead of the cookie endpoint.
 */
export function registerRefreshStrategy(
  handler: (() => Promise<void>) | null,
): void {
  refreshStrategy = handler;
}

/**
 * 403 session-gate handlers — ported from v1
 * ``ando-frontend/app/api/auth/userAuth.ts`` (``markAccountInactive`` /
 * ``markSubscriptionRequired``, lines 390-415).
 *
 * v1 implementation lived inside ``userAuth.ts`` and directly mutated the
 * ``user_info`` cookie + ``window.location.reload()`` with a per-key
 * sessionStorage guard. In v2 the cookie write + reload is host-specific
 * (Next.js BFF vs Tauri webview), so this module owns ONLY the dispatch —
 * the host app subscribes via ``registerSessionGateHandler()`` and decides
 * whether to flip the cookie + reload (web) or push to ``/inactive`` /
 * ``/subscribe`` routes (TanStack Router).
 *
 * Detail vocabulary mirrors v1:
 *   - ``"account inactive"``  → ``"account_inactive"`` reason
 *   - ``"subscription required"`` → ``"subscription_required"`` reason
 * Also accepts the structured ``error_code`` form (``ACCOUNT_INACTIVE`` /
 * ``SUBSCRIPTION_REQUIRED``) emitted by the FastAPI ``AppError`` handler.
 */
export type SessionGateReason = "account_inactive" | "subscription_required";

let onSessionGateHandler: ((reason: SessionGateReason) => void) | null = null;

export function registerSessionGateHandler(
  handler: ((reason: SessionGateReason) => void) | null,
): void {
  onSessionGateHandler = handler;
}

/** @internal Dispatched by ``ApiClient`` on 403 with a known gate token. */
export function emitSessionGate(reason: SessionGateReason): void {
  onSessionGateHandler?.(reason);
}

/**
 * Register a callback invoked when a refresh attempt fails terminally.
 *
 * Typical usage from the host app:
 * ```ts
 * registerAuthFailureHandler((err) => {
 *   useAuthStore.getState().clearSession();
 *   router.navigate({ to: "/login" });
 * });
 * ```
 */
export function registerAuthFailureHandler(handler: ((error: Error) => void) | null): void {
  onAuthFailureHandler = handler;
}

/** Resolve when refresh completes, reject if it fails. Coalesces concurrent calls. */
export function refreshAccessToken(): Promise<void> {
  if (isRefreshing) {
    return new Promise<void>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  return new Promise<void>((resolve, reject) => {
    void (async () => {
      try {
        if (refreshStrategy) {
          // Tauri path — refresh strategy owns the wire format (calls
          // ``/auth/desktop/refresh`` with the OS-keychain refresh_token
          // and persists the rotated tuple). Throws on failure.
          await refreshStrategy();
        } else {
          const apiUrl = getEnv().apiUrl;
          const response = await fetch(`${apiUrl}/auth/refresh`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (!response.ok) {
            throw new Error(`Token refresh failed: ${response.status}`);
          }
        }

        resolve();
        for (const queued of refreshQueue) queued.resolve();
      } catch (err) {
        const refreshError = err instanceof Error ? err : new Error("Token refresh failed");
        reject(refreshError);
        for (const queued of refreshQueue) queued.reject(refreshError);
        if (onAuthFailureHandler) onAuthFailureHandler(refreshError);
      } finally {
        isRefreshing = false;
        refreshQueue = [];
      }
    })();
  });
}

/** Drop-in replacement for ``fetch()`` with automatic 401 → refresh → retry. */
export async function fetchWithRefresh(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const opts: RequestInit = { ...init, credentials: "include" };
  const response = await fetch(input, opts);

  if (response.status !== 401) return response;

  const urlString = typeof input === "string" ? input : input.toString();
  if (isAuthEndpoint(urlString)) return response;

  try {
    await refreshAccessToken();
    return await fetch(input, opts);
  } catch {
    return response;
  }
}
